import React, { useState, useEffect } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import {
  Save,
  Check,
  AlertCircle,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { integrationConfigService } from "@/services/integrationConfigService";
import {
  SiSlack,
  SiGmail,
  SiGithub,
  SiLinear,
  SiTrello,
  SiHubspot,
} from "react-icons/si";

// Integration definitions
const INTEGRATIONS = [
  {
    id: "gmail",
    name: "Gmail",
    icon: <SiGmail className="h-5 w-5 text-[#EA4335]" />,
    description: "Email automation and management",
    docsUrl: "https://docs.composio.dev/apps/gmail",
  },
  {
    id: "slack",
    name: "Slack",
    icon: <SiSlack className="h-5 w-5 text-[#4A154B]" />,
    description: "Team communication and notifications",
    docsUrl: "https://docs.composio.dev/apps/slack",
  },
  {
    id: "github",
    name: "GitHub",
    icon: <SiGithub className="h-5 w-5 text-[#181717]" />,
    description: "Code repository management",
    docsUrl: "https://docs.composio.dev/apps/github",
  },
  {
    id: "linear",
    name: "Linear",
    icon: <SiLinear className="h-5 w-5 text-[#5E6AD2]" />,
    description: "Issue tracking and project management",
    docsUrl: "https://docs.composio.dev/apps/linear",
  },
  {
    id: "trello",
    name: "Trello",
    icon: <SiTrello className="h-5 w-5 text-[#0079BF]" />,
    description: "Visual project boards",
    docsUrl: "https://docs.composio.dev/apps/trello",
  },
  {
    id: "hubspot",
    name: "HubSpot",
    icon: <SiHubspot className="h-5 w-5 text-[#FF7A59]" />,
    description: "CRM and marketing automation",
    docsUrl: "https://docs.composio.dev/apps/hubspot",
  },
];

interface AuthConfig {
  tool: string;
  auth_config_id: string;
  is_enabled: boolean;
  updated_at?: string;
}

const IntegrationsAdminPage = () => {
  const [configs, setConfigs] = useState<Record<string, AuthConfig>>({});
  const [editingConfigs, setEditingConfigs] = useState<Record<string, string>>(
    {},
  );
  const [showConfigs, setShowConfigs] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);

      const data = await integrationConfigService.getGlobalConfigs();

      const configMap: Record<string, AuthConfig> = {};
      const editMap: Record<string, string> = {};

      data.forEach((config) => {
        configMap[config.tool] = {
          tool: config.tool,
          auth_config_id: config.auth_config_id,
          is_enabled: config.is_enabled,
          updated_at: config.updated_at,
        };
        editMap[config.tool] = config.auth_config_id;
      });

      setConfigs(configMap);
      setEditingConfigs(editMap);
    } catch (error: any) {
      console.error("Failed to load configs:", error);
      toast({
        title: "Error",
        description:
          error?.message || "Failed to load integration configurations",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const validateAuthConfigId = (authConfigId: string): string | null => {
    if (!authConfigId?.trim()) {
      return "Auth Config ID is required";
    }

    const trimmed = authConfigId.trim();

    // Check if it starts with expected prefix
    if (!trimmed.startsWith("ac_")) {
      return "Auth Config ID must start with 'ac_'";
    }

    // Check minimum length (ac_ + at least 8 characters)
    if (trimmed.length < 11) {
      return "Auth Config ID appears to be too short";
    }

    // Check for valid characters (alphanumeric and underscores after ac_)
    const validPattern = /^ac_[a-zA-Z0-9_]+$/;
    if (!validPattern.test(trimmed)) {
      return "Auth Config ID contains invalid characters";
    }

    return null; // Valid
  };

  const saveConfig = async (tool: string) => {
    const authConfigId = editingConfigs[tool]?.trim();

    const validationError = validateAuthConfigId(authConfigId || "");
    if (validationError) {
      toast({
        title: "Validation Error",
        description: validationError,
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving(tool);

      const data = await integrationConfigService.upsertGlobalConfig(
        tool,
        authConfigId!,
        configs[tool]?.is_enabled ?? true,
        user?.id,
      );

      setConfigs((prev) => ({
        ...prev,
        [tool]: {
          tool: data.tool,
          auth_config_id: data.auth_config_id,
          is_enabled: data.is_enabled,
          updated_at: data.updated_at,
        },
      }));

      toast({
        title: "Success",
        description: `${INTEGRATIONS.find((i) => i.id === tool)?.name} configuration saved`,
      });
    } catch (error: any) {
      console.error("Failed to save config:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to save configuration",
        variant: "destructive",
      });
    } finally {
      setSaving(null);
    }
  };

  const toggleEnabled = async (tool: string) => {
    const config = configs[tool];
    if (!config) return;

    try {
      await integrationConfigService.toggleGlobalConfig(
        tool,
        !config.is_enabled,
        user?.id,
      );

      setConfigs((prev) => ({
        ...prev,
        [tool]: {
          ...prev[tool],
          is_enabled: !prev[tool].is_enabled,
        },
      }));
    } catch (error: any) {
      console.error("Failed to toggle config:", error);
      toast({
        title: "Error",
        description: error?.message || "Failed to update configuration",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: `${label} copied to clipboard`,
    });
  };

  const toggleShowConfig = (tool: string) => {
    setShowConfigs((prev) => ({
      ...prev,
      [tool]: !prev[tool],
    }));
  };

  const configuredCount = Object.keys(configs).length;
  const enabledCount = Object.values(configs).filter(
    (c) => c.is_enabled,
  ).length;

  return (
    <MainLayout>
      <SuperAdminLayout
        title="Integration Configuration"
        description="Configure Composio Auth Config IDs for each integration. These settings apply globally to all organizations."
      >
        <div className="space-y-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Status Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold">
                      {INTEGRATIONS.length}
                    </div>
                    <p className="text-xs text-gray-500">
                      Available Integrations
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-green-600">
                      {configuredCount}
                    </div>
                    <p className="text-xs text-gray-500">Configured</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-2xl font-bold text-blue-600">
                      {enabledCount}
                    </div>
                    <p className="text-xs text-gray-500">Enabled</p>
                  </CardContent>
                </Card>
              </div>

              {/* Info Alert */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>How to get Auth Config IDs:</strong> Visit your{" "}
                  <a
                    href="https://app.composio.dev/apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-600"
                  >
                    Composio Dashboard
                  </a>
                  , create an app integration, and copy the Auth Config ID
                  (starts with "ac_").
                </AlertDescription>
              </Alert>

              {/* Integration Cards */}
              <div className="space-y-4">
                {INTEGRATIONS.map((integration) => {
                  const config = configs[integration.id];
                  const isConfigured = !!config;
                  const isEnabled = config?.is_enabled ?? false;
                  const currentValue = editingConfigs[integration.id] || "";
                  const hasUnsavedChanges =
                    currentValue !== (config?.auth_config_id || "");
                  const showConfig = showConfigs[integration.id];

                  return (
                    <Card
                      key={integration.id}
                      className={!isEnabled && isConfigured ? "opacity-75" : ""}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {integration.icon}
                            <div>
                              <CardTitle className="text-lg">
                                {integration.name}
                              </CardTitle>
                              <CardDescription className="text-xs">
                                {integration.description}
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isConfigured && (
                              <>
                                <Badge
                                  variant={isEnabled ? "default" : "secondary"}
                                  className="text-xs"
                                >
                                  {isEnabled ? "Enabled" : "Disabled"}
                                </Badge>
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() =>
                                    toggleEnabled(integration.id)
                                  }
                                  aria-label={`Toggle ${integration.name}`}
                                />
                              </>
                            )}
                            {!isConfigured && (
                              <Badge variant="outline" className="text-xs">
                                Not Configured
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                window.open(integration.docsUrl, "_blank")
                              }
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Input
                                type={showConfig ? "text" : "password"}
                                placeholder="Enter Auth Config ID (e.g., ac_...)"
                                value={currentValue}
                                onChange={(e) =>
                                  setEditingConfigs((prev) => ({
                                    ...prev,
                                    [integration.id]: e.target.value,
                                  }))
                                }
                                className={`pr-20 font-mono text-sm ${
                                  currentValue &&
                                  validateAuthConfigId(currentValue)
                                    ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                                    : currentValue &&
                                        !validateAuthConfigId(currentValue)
                                      ? "border-green-300 focus:border-green-500 focus:ring-green-200"
                                      : ""
                                }`}
                              />
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {currentValue &&
                                  validateAuthConfigId(currentValue) && (
                                    <div
                                      className="text-red-500"
                                      title={
                                        validateAuthConfigId(currentValue) || ""
                                      }
                                    >
                                      <X className="h-3 w-3" />
                                    </div>
                                  )}
                                {currentValue &&
                                  !validateAuthConfigId(currentValue) && (
                                    <div className="text-green-500">
                                      <Check className="h-3 w-3" />
                                    </div>
                                  )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    toggleShowConfig(integration.id)
                                  }
                                  className="h-7 w-7 p-0"
                                >
                                  {showConfig ? (
                                    <EyeOff className="h-3 w-3" />
                                  ) : (
                                    <Eye className="h-3 w-3" />
                                  )}
                                </Button>
                                {currentValue && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      copyToClipboard(
                                        currentValue,
                                        "Auth Config ID",
                                      )
                                    }
                                    className="h-7 w-7 p-0"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            </div>
                            {currentValue &&
                              validateAuthConfigId(currentValue) && (
                                <p className="text-sm text-red-600 mt-1">
                                  {validateAuthConfigId(currentValue)}
                                </p>
                              )}
                            <Button
                              onClick={() => saveConfig(integration.id)}
                              disabled={
                                !currentValue ||
                                saving === integration.id ||
                                !hasUnsavedChanges ||
                                !!validateAuthConfigId(currentValue)
                              }
                              className="min-w-[100px]"
                            >
                              {saving === integration.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              ) : hasUnsavedChanges ? (
                                <>
                                  <Save className="h-4 w-4 mr-2" />
                                  Save
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4 mr-2" />
                                  Saved
                                </>
                              )}
                            </Button>
                          </div>

                          {config?.updated_at && (
                            <p className="text-xs text-gray-500">
                              Last updated:{" "}
                              {new Date(config.updated_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Environment Variables Info */}
              <Card className="border-dashed">
                <CardHeader>
                  <CardTitle className="text-base">
                    Environment Variable Fallback
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    As a fallback, you can also set Auth Config IDs via
                    environment variables:
                  </p>
                  <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs space-y-1">
                    {INTEGRATIONS.map((i) => (
                      <div key={i.id} className="flex items-center gap-2">
                        <code>
                          COMPOSIO_{i.id.toUpperCase()}_AUTH_CONFIG_ID=ac_...
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              `COMPOSIO_${i.id.toUpperCase()}_AUTH_CONFIG_ID=`,
                              "Environment variable",
                            )
                          }
                          className="h-6 w-6 p-0"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default IntegrationsAdminPage;
